import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ApiService, AppSettings } from '../services/api';
import { RootState } from './index';

// Helper function to create ApiService with current token
const createApiService = (getState: () => RootState) => {
    const state = getState();
    const apiService = new ApiService(
        process.env.REACT_APP_API_URL || 'http://localhost:3051',
        process.env.REACT_APP_API_URL || 'http://localhost:3051/api'
    );
    // Use token from Redux state, or fallback to localStorage if state hasn't been hydrated yet
    const token = state.auth.token || localStorage.getItem('token');
    if (token) {
        apiService.setToken(token);
    }
    return apiService;
};

interface SettingsState {
    settings: AppSettings | null;
    loading: boolean;
    error: string | null;
}

const initialState: SettingsState = {
    settings: null,
    loading: false,
    error: null,
};

// Async thunks
export const fetchSettings = createAsyncThunk(
    'settings/fetchSettings',
    async (_, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.fetchSettings();
    }
);

export const updateSettings = createAsyncThunk(
    'settings/updateSettings',
    async (settings: AppSettings, { getState }) => {
        const apiService = createApiService(getState as () => RootState);
        return await apiService.updateSettings(settings);
    }
);

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch settings
            .addCase(fetchSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.settings = action.payload;
            })
            .addCase(fetchSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch settings';
            })
            // Update settings
            .addCase(updateSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.settings = action.payload;
            })
            .addCase(updateSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to update settings';
            });
    },
});

export default settingsSlice.reducer;
